import { Button, Drawer, Stack, styled, TextField, Typography } from "~/common/mui";
import { Controller, type FieldError, useForm } from "react-hook-form";
import { DialogHeader, DrawerContent, DrawerHeader } from "./GuestCheckoutCommon";
import { useIsMinTabletSize } from "~/common/hooks/useMediaQuery";
import { SimpleAutocomplete } from "~/common/components/Select/SimpleAutocompleteSelect";
import { Trans, useTranslation } from "react-i18next";
import { useCountryOptions } from "~/features/cart/B2CRequestPage/utils";
import { apiUserExists } from "~/features/cart/B2CCart/components/GuestCheckoutDialog/api/userExists";
import { useMemo, useState } from "react";
import debounce from "lodash/debounce";
import { LinkButton } from "~/common/components/Link";
import { useSalutationOptions } from "~/features/scanDmc/components/ContactFormOverlay/dmcContactFormUtils";
import { SimpleSelect } from "~/common/components/Select/SimpleSelect";
import { makeSelectRef4RHF } from "~/features/myAccount/profile/formUtils";
import { STICKY_TO_BOTTOM } from "~/common/components/icon/Elevation";
import { Dialog, DialogActions, DialogContent, DialogTitle } from "~/common/mui/Dialog/Dialog";
import { EMAIL_PATTERN } from "~/utils/constants";
import { PHONE_REGEXP } from "~/common/utils/validations";

export interface GuestCheckoutFormData {
  firstName: string;
  lastName: string;
  salutationCode: string;
  companyName: string;
  email: string;
  phone: string;
  line1: string;
  line2: string;
  zipCode: string;
  city: string;
  country: string;
}

export type GuestCheckoutFormModalProps = {
  open: boolean;
  onClose: () => void;
  onSubmit: (formData: GuestCheckoutFormData) => void;
  onLoginClick: (email: string) => void;
};

const defaultValues: GuestCheckoutFormData = {
  companyName: "",
  firstName: "",
  lastName: "",
  salutationCode: "",
  email: "",
  phone: "",
  line1: "",
  line2: "",
  zipCode: "",
  city: "",
  country: "",
};

const GuestCheckoutFormModal = ({ open, onClose, onSubmit, onLoginClick }: GuestCheckoutFormModalProps) => {
  const { t } = useTranslation();
  const renderDialog = useIsMinTabletSize();
  const { handleSubmit, control, getValues } = useForm<GuestCheckoutFormData>({ defaultValues });
  const countryOptions = useCountryOptions();

  const [emailExists, setEmailExists] = useState(false);
  const onEmailChange = useMemo(() => {
    return debounce(async (email: string) => {
      setEmailExists(false);
      const exists = await apiUserExists(email);
      setEmailExists(exists);
    }, 500);
  }, [setEmailExists]);
  const onEmailBlur = onEmailChange.flush;

  const handleLoginClick = () => {
    onLoginClick(getValues("email"));
  };

  const salutationOptions = useSalutationOptions();

  const renderEmailHelperText = (error: FieldError | undefined) => {
    if (emailExists) {
      return (
        <Trans>
          There is already an account existing with this email. You can still checkout as a guest or{" "}
          <LinkButton onClick={handleLoginClick}>login to your account</LinkButton>.
        </Trans>
      );
    }
    if (error?.message) {
      return error.message;
    }
    return null;
  };

  const content = (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Stack gap={3}>
        <Stack gap={2}>
          <Typography variant="bodyLarge">{t("page.selfRegistration.step.2.label")}</Typography>
          <Controller
            name="companyName"
            control={control}
            rules={{ required: true }}
            render={({ field, fieldState: { error } }) => (
              <TextField
                {...field}
                label={t("page.selfRegistration.field.companyName.label")}
                required
                fullWidth
                error={!!error}
              />
            )}
          />

          <Controller
            name={"salutationCode"}
            control={control}
            render={({ field: { value, onChange, onBlur, ref }, fieldState: { error } }) => (
              <SimpleSelect
                required
                values={salutationOptions}
                label={t("page.completeRegistration.salutation")}
                error={!!error}
                fullWidth={true}
                onBlur={onBlur}
                value={value}
                onChange={(e) => onChange(e.target.value)}
                ref={makeSelectRef4RHF(ref)}
                helperText={error?.message ?? ""}
              />
            )}
            rules={{
              required: true,
            }}
          />

          <Stack direction={{ phone: "column", "desktop-s": "row" }} gap={2}>
            <Controller
              name="firstName"
              control={control}
              rules={{ required: true }}
              render={({ field, fieldState: { error } }) => (
                <TextField
                  {...field}
                  label={t("page.completeRegistration.firstName")}
                  required
                  fullWidth
                  error={!!error}
                />
              )}
            />
            <Controller
              name="lastName"
              control={control}
              rules={{ required: true }}
              render={({ field, fieldState: { error } }) => (
                <TextField
                  {...field}
                  label={t("page.completeRegistration.lastName")}
                  required
                  fullWidth
                  error={!!error}
                />
              )}
            />
          </Stack>
          <Controller
            name="email"
            control={control}
            rules={{
              required: true,
              pattern: { value: EMAIL_PATTERN, message: t("error.invalidEmail") },
            }}
            render={({ field, fieldState: { error } }) => (
              <TextField
                {...field}
                label={t("page.completeRegistration.email")}
                required
                fullWidth
                onBlur={onEmailBlur}
                onChange={(e) => {
                  field.onChange(e);
                  onEmailChange(e.target.value);
                }}
                error={!!error}
                helperText={renderEmailHelperText(error)}
              />
            )}
          />
          <Controller
            name="phone"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                label={t("page.completeRegistration.phone")}
                fullWidth
                inputProps={{
                  onBeforeInput: (e) => {
                    const event = e as unknown as InputEvent;
                    const val = (e.target as HTMLInputElement).value + event.data;
                    if (!PHONE_REGEXP.test(val.trim())) {
                      e.preventDefault();
                      e.stopPropagation();
                    }
                  },
                }}
                autoComplete={"tel"}
              />
            )}
          />
        </Stack>
        <Stack gap={2}>
          <Typography variant="bodyLarge">{t("feature.guestCheckout.guestForm.addressInformation")}</Typography>
          <Controller
            name="line1"
            control={control}
            rules={{ required: true }}
            render={({ field, fieldState: { error } }) => (
              <TextField {...field} label={t("page.completeRegistration.address")} required fullWidth error={!!error} />
            )}
          />
          <Controller
            name="line2"
            control={control}
            render={({ field }) => (
              <TextField {...field} label={t("page.completeRegistration.addressAdditional")} fullWidth />
            )}
          />
          <Stack direction={{ phone: "column", "desktop-s": "row" }} gap={2}>
            <Controller
              name="zipCode"
              control={control}
              rules={{ required: true }}
              render={({ field, fieldState: { error } }) => (
                <TextField
                  {...field}
                  label={t("page.completeRegistration.zipCode")}
                  required
                  fullWidth
                  error={!!error}
                />
              )}
            />
            <Controller
              name="city"
              control={control}
              rules={{ required: true }}
              render={({ field, fieldState: { error } }) => (
                <TextField {...field} label={t("page.completeRegistration.city")} required fullWidth error={!!error} />
              )}
            />
          </Stack>
          <Controller
            name={"country"}
            control={control}
            rules={{ required: { value: true, message: t("validation.country.missing") } }}
            defaultValue={""}
            render={({ field: { value, onChange, onBlur }, fieldState: { error } }) => (
              <SimpleAutocomplete
                label={t("STCfinder.table.header.country") + " *"}
                data-test-id={"complete-registration-country"}
                inputProps={{
                  required: true,
                }}
                values={countryOptions}
                fullWidth={true}
                error={!!error}
                helperText={error?.message ?? " "}
                onBlur={onBlur}
                value={value}
                onChange={(value) => onChange(value)}
              />
            )}
          />
        </Stack>
      </Stack>
    </form>
  );

  const dialog = renderDialog ? (
    <Dialog open={open} onClose={onClose} maxWidth={"desktop-s"}>
      <DialogHeader onClose={onClose}>
        <DialogTitle sx={{ padding: 0 }}>{t("feature.guestCheckout.guestForm.enterInformation")}</DialogTitle>
      </DialogHeader>
      <DialogContent>{content}</DialogContent>
      <DialogActions>
        <Button type="submit" onClick={handleSubmit(onSubmit)}>
          {t("page.cart.b2c.btn.continue")}
        </Button>
      </DialogActions>
    </Dialog>
  ) : (
    <Drawer anchor="bottom" open={open} onClose={onClose}>
      <DrawerHeader onClose={onClose}>
        <Typography variant="titleLarge">{t("feature.guestCheckout.guestForm.enterInformation")}</Typography>
      </DrawerHeader>
      <DrawerContent>{content}</DrawerContent>
      <DrawerActions>
        <Button type="submit" fullWidth onClick={handleSubmit(onSubmit)}>
          {t("page.cart.b2c.btn.continue")}
        </Button>
      </DrawerActions>
    </Drawer>
  );

  return dialog;
};

export default GuestCheckoutFormModal;

export const DrawerActions = styled(DialogActions)({
  padding: "8px 16px",
  boxShadow: STICKY_TO_BOTTOM,
});
